/**
 * RAV4 (FBX 경로) 전용 이름 기반 색 보정.
 *
 * FBX 가 진짜 머티리얼 색을 안 담아(전부 회색 0.5) 역할 프리셋만으론 차가 단색이 된다.
 * 머티리얼 "이름"이 색 의도를 담고 있으므로(C_Outer_black_, C_glossy_black_, C_1M6_
 * 차체 도장코드, C_white_ …) 이름으로 baseColor + 약간의 PBR 을 보정한다.
 * applyRoleOverrides 직후 호출 — 역할이 잡은 metalness/clearcoat 위에 색만 덮는다.
 *
 * 800B 무관 (test-rav4 에서만 호출). 정확한 공장색/텍스처는 Maya 2024 한계로 제외.
 */
import * as THREE from 'three';
import { stripMayaNamespace } from '../utils/mayaNamespace.js';

export interface Rav4TuneOpts {
  /** 차체 도장 기본색 (페인트 UI 미연결 시 hero 색). */
  bodyColor: number;
}

const BODY_PAINT = /^C_1M6_/; // GR-S 차체 도장 코드 (면적 최대 외장 패널)

export function tuneRav4Materials(
  root: THREE.Object3D,
  opts: Rav4TuneOpts = { bodyColor: 0xdedede },
): { recolored: number } {
  let recolored = 0;
  const seen = new Set<THREE.Material>();
  root.traverse((o) => {
    if (!(o instanceof THREE.Mesh)) return;
    const arr = Array.isArray(o.material) ? o.material : [o.material];
    for (const m of arr) {
      if (!m || seen.has(m)) continue;
      seen.add(m);
      const mat = m as THREE.MeshStandardMaterial & { clearcoat?: number; clearcoatRoughness?: number };
      if (!mat.color) continue;
      const n = stripMayaNamespace(mat.name);
      const low = n.toLowerCase();
      const hasCoat = 'clearcoat' in mat;

      if (BODY_PAINT.test(n)) {
        // 차체 → 광택 페인트
        mat.color.setHex(opts.bodyColor);
        mat.metalness = 0.9; mat.roughness = 0.45;
        if (hasCoat) { mat.clearcoat = 1; mat.clearcoatRoughness = 0.03; }
      } else if (low.includes('glossy_black') || low.includes('glossyblack')) {
        mat.color.setHex(0x0a0a0a); mat.roughness = 0.2;
        if (hasCoat) mat.clearcoat = 1;
      } else if (low.includes('silver') || low.includes('hairline')) {
        mat.color.setHex(0xb4b4b4); mat.metalness = 1; mat.roughness = 0.35;
      } else if (low.includes('black') || /_bk\d/.test(low) || low.includes('kuro') || low.includes('outer_black') || low.includes('interior_black')) {
        mat.color.setHex(0x18181a); mat.roughness = 0.55;
      } else if (low.includes('white') || low.includes('_040_') || low.includes('mark_white')) {
        mat.color.setHex(0xe6e6e6);
      } else {
        continue;
      }
      mat.needsUpdate = true;
      recolored += 1;
    }
  });
  return { recolored };
}
